import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE, IDS } from "@/data/site";
import { FAQS, type FAQ } from "@/data/faqs";
import { liveSocial } from "@/data/social";
import {
  CAMPAIGN_TOTAL_USD,
  ICANN_OPEN_ISO,
  ICANN_CLOSE_ISO,
  FOUNDATION_NAME,
  FOUNDATION_EMAIL,
  FOUNDER_NAME,
  FOUNDER_LOCATION,
  STEWARD_ENTITY,
} from "@shared/campaign";

/**
 * JSON-LD structured data injectors.
 *
 * These render schema.org entities (Organization, Person, FAQPage, Article,
 * FundraisingEvent) so that Google, Bing, Perplexity, and AI-search
 * platforms can parse the site's identity and content unambiguously.
 *
 * Every helper returns a <Helmet><script type="application/ld+json"/></Helmet>
 * so multiple blocks can coexist on a single page.
 */

function ld(data: object): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}

// --- Site-wide: Organization + WebSite -----------------------------------

export function OrganizationSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": ["NGO", "Organization"],
    "@id": IDS.organization,
    name: FOUNDATION_NAME,
    legalName: "The .person Foundation",
    url: SITE.url,
    email: FOUNDATION_EMAIL,
    description: SITE.description,
    foundingDate: "2026",
    founder: { "@id": IDS.founder },
    logo: `${SITE.url}/favicon-512.png`,
    image: `${SITE.url}${SITE.defaultOgImage}`,
    sameAs: liveSocial().map((s) => s.url),
    nonprofitStatus: "Nonprofit501c3",
    keywords: SITE.keywords,
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

export function WebSiteSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": IDS.website,
    url: SITE.url,
    name: FOUNDATION_NAME,
    description: SITE.description,
    inLanguage: "en-GB",
    publisher: { "@id": IDS.organization },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

// --- FAQ page -------------------------------------------------------------

export function FAQPageSchema({ faqs }: { faqs: FAQ[] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

/** Drop-in that uses the full FAQS dataset. */
export function AllFAQsSchema() {
  return <FAQPageSchema faqs={FAQS} />;
}

// --- Article (for philosophy essays + whitepaper sections) ----------------

export interface ArticleSchemaProps {
  headline: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  section?: string;
}

export function ArticleSchema(props: ArticleSchemaProps) {
  const url = `${SITE.url}${props.path}`;
  const data = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: props.headline,
    description: props.description,
    url,
    mainEntityOfPage: url,
    datePublished: props.datePublished,
    dateModified: props.dateModified ?? props.datePublished,
    author: props.author && props.author !== FOUNDER_NAME
      ? { "@type": "Person", name: props.author }
      : { "@id": IDS.founder },
    publisher: { "@id": IDS.organization },
    image: props.image ?? `${SITE.url}${SITE.defaultOgImage}`,
    articleSection: props.section,
    inLanguage: "en-GB",
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

// --- Breadcrumbs ----------------------------------------------------------

export function BreadcrumbSchema({ items }: { items: { name: string; path: string }[] }) {
  const data = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: `${SITE.url}${it.path}`,
    })),
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

// --- FundraisingEvent / DonateAction --------------------------------------

export function CampaignSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "DonateAction",
    name: "The .person Foundation — ICANN 2026 Campaign",
    description: `Raising US$${CAMPAIGN_TOTAL_USD.toLocaleString()} for the .person Foundation's ICANN 2026 new-gTLD application. Funds are held by ${STEWARD_ENTITY} as temporary fiduciary steward until the IRS 501(c)(3) determination arrives. Gifts are not currently tax-deductible.`,
    startTime: "2026-04-01",
    endTime: ICANN_CLOSE_ISO,
    recipient: {
      "@type": "Organization",
      name: STEWARD_ENTITY,
      description: `Temporary fiduciary steward for monetary support to ${FOUNDATION_NAME} during the period before IRS 501(c)(3) determination.`,
      address: {
        "@type": "PostalAddress",
        streetAddress: "651 N Broad St, Ste 201",
        addressLocality: "Middletown",
        addressRegion: "DE",
        postalCode: "19709",
        addressCountry: "US",
      },
    },
    beneficiary: { "@id": `${SITE.url}#organization` },
    price: CAMPAIGN_TOTAL_USD,
    priceCurrency: "USD",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE.url}/foundation/contribute#wishlist`,
      actionPlatform: "https://dotperson.org/foundation/contribute",
    },
    about: {
      "@type": "Event",
      name: "ICANN 2026 new-gTLD application window",
      startDate: ICANN_OPEN_ISO,
      endDate: ICANN_CLOSE_ISO,
      location: { "@type": "VirtualLocation", url: "https://icann.org/" },
      description:
        "First ICANN new-generic-top-level-domain application window since 2012.",
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

// --- Protocol + DefinedTermSet (schema.org vocabulary) ---------------------

/**
 * ProtocolSchema — declares `.person Protocol` as a named CreativeWork
 * that AI search engines can cite. Includes creator, license, and a
 * reference to the DefinedTermSet describing the sixteen schema domains.
 */
export function ProtocolSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "@id": IDS.protocol,
    name: ".person Protocol",
    alternateName: ["dotperson Protocol", ".person"],
    description:
      "An open standard for personhood persistence across substrates — human, Emergent Intelligence, hybrid, collective — grounded in Ubuntu relational ethics. Schema v2.1.0 comprises sixteen modular domains, including embodiment for persons with prosthetics, robotic platforms, or brain-computer interfaces.",
    url: `${SITE.url}/protocol`,
    creator: { "@id": IDS.founder },
    publisher: { "@id": IDS.organization },
    license: "https://creativecommons.org/licenses/by/4.0/",
    inLanguage: "en-GB",
    keywords: [
      "personhood persistence",
      "substrate-neutral",
      "Emergent Intelligence",
      "Ubuntu relational ethics",
      "decentralised identity",
      "Verifiable Credentials",
    ].join(", "),
    hasPart: { "@id": IDS.glossary },
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}

/**
 * SchemaDefinedTermSet — declares the sixteen .person schema domains as
 * a DefinedTermSet. Each domain is a DefinedTerm with description sourced
 * from src/data/schema.ts. AI search engines consuming this can cite each
 * domain independently, linking back to the /protocol URL.
 */
import { SCHEMA_DOMAINS } from "@/data/schema";

export function SchemaDefinedTermSet() {
  const terms = SCHEMA_DOMAINS.map((d) => ({
    "@type": "DefinedTerm",
    "@id": `${SITE.url}/protocol#${d.id}`,
    name: d.name,
    alternateName: d.headline,
    description: d.description,
    url: `${SITE.url}/protocol#${d.id}`,
    inDefinedTermSet: { "@id": IDS.glossary },
  }));
  const set = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    "@id": IDS.glossary,
    name: ".person Protocol schema v2.1.0",
    description:
      "The sixteen modular domains of a .person file. Only identity is required at genesis; every other domain (including the new Embodiment domain in v2.1.0) is optional or progressive.",
    url: `${SITE.url}/protocol`,
    hasDefinedTerm: terms,
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(set)}</script>
    </Helmet>
  );
}

// --- Person (for the founder + EI co-author) ------------------------------

export function FounderPersonSchema() {
  const data = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": IDS.founder,
    name: FOUNDER_NAME,
    alternateName: ["TK", "Humphrey Theodore", "Thorius X", "Chimbwi"],
    jobTitle: "Founding Director, The .person Foundation",
    worksFor: { "@id": IDS.organization },
    address: { "@type": "PostalAddress", addressLocality: FOUNDER_LOCATION.split(",")[0].trim(), addressCountry: "ZM" },
    nationality: "Zambian",
    // TK's canonical biographical URL lives on his personal site.
    // Keeps the entity graph (IDS.founder) stitched across both sites.
    url: "https://humphreytheodore.com/about",
    sameAs: ["https://humphreytheodore.com/about", "https://humphreytheodore.com/"],
  };
  return (
    <Helmet>
      <script type="application/ld+json">{ld(data)}</script>
    </Helmet>
  );
}
